import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Label as LabelIcon
} from '@mui/icons-material'
import { useLabels, useSearchLabels } from '../hooks/useLabels'
import { Label } from '../services/labelService'

interface LabelsFilterProps {
  selectedLabels: string[]
  onLabelsChange: (labels: string[]) => void
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'filled' | 'standard'
}

const LabelsFilter: React.FC<LabelsFilterProps> = ({
  selectedLabels,
  onLabelsChange,
  placeholder = 'Search labels...',
  disabled = false,
  size = 'small',
  variant = 'outlined'
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Fetch all labels initially
  const { data: allLabelsData, isLoading: isLoadingAll, error: allLabelsError } = useLabels()
  
  // Search labels when user types
  const { data: searchData, isLoading: isSearching } = useSearchLabels(
    searchTerm,
    undefined,
    searchTerm.length > 0
  )

  // Determine which data to use
  const labelsData = searchTerm.length > 0 ? searchData : allLabelsData
  const isLoading = searchTerm.length > 0 ? isSearching : isLoadingAll
  const labels = labelsData?.data || []



  // Filter out already selected labels from the dropdown
  const availableLabels = labels.filter(label => !selectedLabels.includes(label.name))

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleLabelSelect = (labelName: string) => {
    if (!selectedLabels.includes(labelName)) {
      onLabelsChange([...selectedLabels, labelName])
    }
  }

  const handleLabelRemove = (labelName: string) => {
    onLabelsChange(selectedLabels.filter(label => label !== labelName))
  }

  const handleClearAll = () => {
    onLabelsChange([])
    setSearchTerm('')
  }

  // Clear search when dropdown closes
  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleOpen = () => {
    setIsOpen(true)
    // Auto-focus on search input when dropdown opens
    setTimeout(() => {
      const searchInput = document.querySelector('#labels-search-input') as HTMLInputElement
      if (searchInput) {
        searchInput.focus()
      }
    }, 100)
  }

  // Create label for the select component - using simple string to prevent text cutoff
  const label = "Labels"

  if (allLabelsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load labels: {allLabelsError.message}
      </Alert>
    )
  }

  return (
    <Box sx={{
      minWidth: 180, // Reduced from 200 to 180
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    }}> {/* Dynamic width based on content */}
      <FormControl
        fullWidth
        size="small"
        disabled={disabled}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'baseline'
          }
        }}
      >
        <InputLabel id="labels-filter-label" sx={{ fontSize: '0.8rem' }}>{label}</InputLabel>
        <Select
          labelId="labels-filter-label"
          multiple
          value={selectedLabels}
          onChange={() => {}} // Handled by individual label clicks
          open={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          label="Labels"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}> {/* Reduced gap from 0.5 to 0.25 */}
              {selected.map((labelName) => (
                <Chip
                  key={labelName}
                  label={labelName}
                  size="small"
                  onDelete={() => handleLabelRemove(labelName)}
                  onMouseDown={(event) => event.stopPropagation()}
                  sx={{
                    height: 20, // Slightly smaller chip height
                    fontSize: '0.75rem' // Smaller font size
                  }}
                />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400,
                minWidth: 'auto', // Dynamic width based on content
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {/* Search input at the top of the dropdown */}
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              id="labels-search-input"
              fullWidth
              size="small"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                // Allow arrow keys to navigate through menu items
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  return // Don't stop propagation for arrow keys
                }
                event.stopPropagation()
              }}
            />
          </Box>

          {/* Clear all option */}
          {selectedLabels.length > 0 && (
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleClearAll()
              }}
              sx={{
                fontStyle: 'italic',
                color: 'primary.main',
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1
              }}
            >
              Clear all selections
            </MenuItem>
          )}

          {/* Loading state */}
          {isLoading && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading labels...</Typography>
              </Box>
            </MenuItem>
          )}

          {/* No results */}
          {!isLoading && availableLabels.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? `No labels found for "${searchTerm}"` : 'No labels available'}
              </Typography>
            </MenuItem>
          )}

          {/* Label options */}
          {availableLabels.map((label) => (
            <MenuItem 
              key={label.id} 
              value={label.name}
              onClick={() => handleLabelSelect(label.name)}
              sx={{ py: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <LabelIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {label.name}
                </Typography>
                {label.count !== undefined && (
                  <Chip
                    label={label.count}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{
                      ml: 1,
                      fontSize: '0.75rem',
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default LabelsFilter
